import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import $api from '../api'
import { GetListDTO } from '../dto'

export type LocaleType = 'en' | 'fr'

export interface PlaylistCategory {
  _id: string
  title: string
  image: string
}

export interface Playlist
  extends Omit<PlaylistBrief, 'songs' | 'moods' | 'businesses' | 'daytimes' | 'styles'> {
  moods?: PlaylistCategory[]
  businesses?: PlaylistCategory[]
  daytimes?: PlaylistCategory[]
  styles?: PlaylistCategory[]
  songs: Song[]
}

export interface PlaylistBrief {
  title: string
  description: {
    [key in LocaleType]?: string
  }
  image: string
  songs: string[]
  totalDuration?: number
  songsCount?: number
  updatedAt?: string
  _id: string
  moods?: string[]
  businesses?: string[]
  daytimes?: string[]
  styles?: string[]
}

export interface PlaylistFolder {
  title: string
  image: string
  _id: string
}

export interface Song {
  _id: string
  title: string
  artist: string
  file: string //filename from uploaded folder
  createdAt: number
  modifiedAt?: number
  duration?: number
  imageUrl?: string
}

interface State {
  playlistsBrief?: PlaylistBrief[]
  playlists: Record<Playlist['_id'], Playlist>
  moods?: PlaylistFolder[]
  businesses?: PlaylistFolder[]
  styles?: PlaylistFolder[]
  daytimes?: PlaylistFolder[]
}

export enum PlaylistOrderBy {
  TITLE = 'title',
}

interface GetListPlaylistsDto extends GetListDTO {
  oderBy?: PlaylistOrderBy
  mood?: string
  business?: string
  daytime?: string
  style?: string
}

export const initialState: State = {
  playlists: {},
}

export const attemptGetPlaylistsList = createAsyncThunk<
  {
    total: number
    playlists: PlaylistBrief[]
  },
  GetListPlaylistsDto
>('playlists/list', async (payload) => {
  const response = await $api.post('playlists/user/public-list', payload)
  return response.data
})

export const attemptGetPlaylistsFolderList = createAsyncThunk<
  {
    total: number
    playlists: PlaylistBrief[]
  },
  GetListPlaylistsDto
>('playlists/folder-list', async (payload) => {
  const response = await $api.post('playlists/user/public-list', payload)
  return response.data
})

export const attemptGetPlaylist = createAsyncThunk<Playlist, string>(
  'playlists/get-playlist',
  async (id) => {
    const response = await $api.post(`playlists/get/${id}`)
    return response.data
  },
)

export const attemptGetMoodsList = createAsyncThunk<
  PlaylistFolder[],
  GetListPlaylistsDto
>('moods/list', async () => {
  const response = await $api.post('moods/user/list')
  return response.data
})

export const attemptGetStylesList = createAsyncThunk<
  PlaylistFolder[],
  GetListPlaylistsDto
>('styles/list', async () => {
  const response = await $api.post('styles/user/list')
  return response.data
})

export const attemptGetDaytimesList = createAsyncThunk<
  PlaylistFolder[],
  GetListPlaylistsDto
>('daytimes/list', async () => {
  const response = await $api.post('daytimes/user/list')
  return response.data
})

export const attemptGetBusinessesList = createAsyncThunk<
  PlaylistFolder[],
  GetListPlaylistsDto
>('businesses/list', async (payload) => {
  const response = await $api.post('businesses/user/list', payload)
  return response.data
})

export const playlistSlice = createSlice({
  name: 'playlist',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(attemptGetPlaylistsList.fulfilled, (state, { payload }) => {
      state.playlistsBrief = payload.playlists
    })
    builder.addCase(attemptGetPlaylist.fulfilled, (state, { payload }) => {
      state.playlists[payload._id] = payload
    })
    builder.addCase(attemptGetMoodsList.fulfilled, (state, { payload }) => {
      state.moods = payload
    })
    builder.addCase(attemptGetStylesList.fulfilled, (state, { payload }) => {
      state.styles = payload
    })
    builder.addCase(attemptGetDaytimesList.fulfilled, (state, { payload }) => {
      state.daytimes = payload
    })
    builder.addCase(attemptGetBusinessesList.fulfilled, (state, { payload }) => {
      state.businesses = payload
    })
  },
})

export default playlistSlice.reducer
